// Захват: raw getUserMedia → AudioWorklet → PCM → WAV-чанки ~5 c → IndexedDB → uploader.

import { CHUNK_SECONDS, buildWavHeader, floatTo16 } from '@besedka/shared';
import * as micDevice from '../lib/micDevice.js';
import { measureServerDelta } from './clock.js';
import { chunkKey, putChunk } from './idb.js';
import { uploader } from './uploader.js';

export interface RecorderStatus {
  active: boolean;
  segmentNo: number | null;
  sampleRate: number;
  level: number; // RMS 0..1
}

export class Recorder {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private pending: Float32Array[] = [];
  private pcmBuffer: Int16Array | null = null;
  private pcmFilled = 0;
  private chunkSamples = 0;
  private seq = 0;
  private segmentId: number | null = null;
  private segmentNo: number | null = null;
  private stopping = false;
  private starting = false;

  status: RecorderStatus = { active: false, segmentNo: null, sampleRate: 0, level: 0 };
  onStatus: (status: RecorderStatus) => void = () => {};
  /** Мьют: захват и счёт сэмплов идут, но в чанки пишутся нули. */
  muted = false;
  /** Время последней пачки сэмплов — watchdog ловит замороженный захват. */
  lastFrameAt = 0;

  get active(): boolean {
    return this.status.active;
  }

  /** Живой sampleRate трека микрофона (не AudioContext): ловит переключение
   * Bluetooth-гарнитуры в HFP (16 кГц) прямо посреди эфира. 0 — трека нет или
   * браузер не отдаёт настройку. */
  get micSampleRate(): number {
    const track = this.mediaStream?.getAudioTracks()[0];
    return track?.getSettings().sampleRate ?? 0;
  }

  /** Название текущего устройства (для карточки здоровья и селектора). */
  get micLabel(): string {
    return this.mediaStream?.getAudioTracks()[0]?.label ?? '';
  }

  /** deviceId фактически захваченного микрофона ('' — браузер не отдаёт). */
  get micDeviceId(): string {
    return this.mediaStream?.getAudioTracks()[0]?.getSettings().deviceId ?? '';
  }

  async start(episodeId: number, participantId: number, epochTs: number): Promise<void> {
    // Флаг starting выставляется синхронно: параллельный второй start()
    // (гонка state.update во время clock-sync/getUserMedia) писал бы два
    // пайплайна вперемешку в один буфер — звук ×2 длиннее с артефактами
    if (this.status.active || this.starting) return;
    this.starting = true;
    try {
      await this.doStart(episodeId, participantId, epochTs);
    } finally {
      this.starting = false;
    }
  }

  private async doStart(episodeId: number, participantId: number, epochTs: number): Promise<void> {
    this.stopping = false;

    const serverDelta = await measureServerDelta();

    // Raw-захват: без AEC/NS/AGC — чистый сигнал для записи (спека §3.1).
    // Выбранный микрофон — через exact deviceId; если устройство исчезло,
    // откатываемся на системный, иначе 5-секундный retry крутился бы вхолостую.
    const baseAudio: MediaTrackConstraints = {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1
    };
    const preferred = micDevice.getAudioConstraint(baseAudio);
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: preferred });
    } catch (error) {
      if (preferred === baseAudio) throw error;
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: baseAudio });
    }

    this.audioContext = new AudioContext();
    await this.audioContext.resume();
    const sampleRate = this.audioContext.sampleRate; // фиксируем что дали
    this.chunkSamples = sampleRate * CHUNK_SECONDS;
    this.pcmBuffer = new Int16Array(this.chunkSamples);
    this.pcmFilled = 0;
    this.seq = 0;
    this.segmentId = null;

    await this.audioContext.audioWorklet.addModule(new URL('./pcm-worklet.js', import.meta.url));
    const source = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 0
    });
    source.connect(this.workletNode);

    let firstBatch = true;
    this.workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
      const samples = event.data;
      this.lastFrameAt = Date.now();
      this.updateLevel(samples); // уровень считаем ДО обнуления — детект «говорит в мьюте»
      // Мьют: в запись идёт тишина той же длительности — счёт сэмплов и
      // таймлайн сегмента не рвутся (это НЕ «дыра» для сборщика).
      if (this.muted) samples.fill(0);
      if (firstBatch) {
        firstBatch = false;
        // Время первого сэмпла ≈ сейчас (по серверным часам) минус длительность пачки
        const firstSampleServerTime = Date.now() + serverDelta - (samples.length / sampleRate) * 1000;
        const epochOffsetMs = Math.max(0, Math.round(firstSampleServerTime - epochTs));
        void this.openSegment(episodeId, participantId, sampleRate, epochOffsetMs);
      }
      if (this.segmentId === null) {
        this.pending.push(samples); // сегмент ещё регистрируется — копим
      } else {
        this.ingest(samples, episodeId, participantId, sampleRate);
      }
    };

    this.lastFrameAt = Date.now();
    this.status = { active: true, segmentNo: null, sampleRate, level: 0 };
    this.onStatus(this.status);
  }

  private async openSegment(
    episodeId: number,
    participantId: number,
    sampleRate: number,
    epochOffsetMs: number
  ): Promise<void> {
    const response = await fetch(`/api/episodes/${episodeId}/segments`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sampleRate, epochOffsetMs })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      this.status = { ...this.status, active: false };
      this.onStatus(this.status);
      throw new Error(body.error || `segment registration failed: HTTP ${response.status}`);
    }
    const { segmentId, segmentNo } = (await response.json()) as { segmentId: number; segmentNo: number };
    this.segmentId = segmentId;
    this.segmentNo = segmentNo;
    this.status = { ...this.status, segmentNo };
    this.onStatus(this.status);
    const buffered = this.pending;
    this.pending = [];
    for (const samples of buffered) this.ingest(samples, episodeId, participantId, sampleRate);
  }

  private updateLevel(samples: Float32Array): void {
    let sum = 0;
    for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
    const rms = Math.sqrt(sum / samples.length);
    this.status = { ...this.status, level: Math.round(rms * 1000) / 1000 };
  }

  private ingest(samples: Float32Array, episodeId: number, participantId: number, sampleRate: number): void {
    if (!this.pcmBuffer) return;
    let offset = 0;
    while (offset < samples.length) {
      const space = this.chunkSamples - this.pcmFilled;
      const take = Math.min(space, samples.length - offset);
      floatTo16(samples.subarray(offset, offset + take)).forEach((v, i) => {
        this.pcmBuffer![this.pcmFilled + i] = v;
      });
      this.pcmFilled += take;
      offset += take;
      if (this.pcmFilled >= this.chunkSamples) {
        void this.flushChunk(episodeId, participantId, sampleRate, false);
      }
    }
  }

  private async flushChunk(
    episodeId: number,
    participantId: number,
    sampleRate: number,
    isFinal: boolean
  ): Promise<void> {
    if (!this.pcmBuffer || this.segmentId === null || this.pcmFilled === 0) return;
    const nSamples = this.pcmFilled;
    const pcm = this.pcmBuffer.slice(0, nSamples);
    this.pcmBuffer = new Int16Array(this.chunkSamples);
    this.pcmFilled = 0;
    const seq = this.seq++;

    const header = buildWavHeader(nSamples, sampleRate);
    const blob = new Blob([header, pcm.buffer], { type: 'audio/wav' });

    // Сначала — локально (IndexedDB), только потом аплоад (спека §3.2)
    await putChunk({
      key: chunkKey(this.segmentId, seq),
      episodeId,
      participantId,
      segmentId: this.segmentId,
      segmentNo: this.segmentNo ?? 0,
      seq,
      nSamples,
      sampleRate,
      status: 'local',
      blob,
      createdAt: Date.now()
    });
    uploader.kick();
    if (isFinal) this.segmentId = null;
  }

  /** Останавливает захват; финальный неполный чанк уходит в IndexedDB. */
  async stop(episodeId?: number, participantId?: number): Promise<void> {
    if (!this.status.active || this.stopping) return;
    this.stopping = true;
    const sampleRate = this.status.sampleRate;

    this.workletNode?.port.close();
    this.workletNode?.disconnect();
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    await this.audioContext?.close().catch(() => {});

    if (episodeId !== undefined && participantId !== undefined) {
      await this.flushChunk(episodeId, participantId, sampleRate, true);
    }

    this.audioContext = null;
    this.mediaStream = null;
    this.workletNode = null;
    this.pending = [];
    this.status = { active: false, segmentNo: null, sampleRate: 0, level: 0 };
    this.onStatus(this.status);
  }
}
